import { DeleteOutlined, FileSyncOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Popover,
  Row,
  Spin,
  Typography,
} from 'antd';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { postGeneracion } from 'api/pae/generaciones';
import { getOrigenesDeCredito } from 'api/pae/origenesDeCredito';
import { getSistemas } from 'api/recaudacion/sistemas';
import { getPadronesMap } from 'api/recaudacion/utils';
import FormSubmitCustomControls from 'components/FormSubmitCustomControls';
import { InputSearchPro } from 'components/InputSearch';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import useConfirm from 'hooks/useConfirm';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { onError } from 'utils/handlers';
import padronToString from 'utils/padronesToString';
import { getPermissionActionsConfigsPadrones, hasAdvanceSearchPermissions, hasPadronPermission } from 'utils/permission/models';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import PropTypes from 'prop-types';
import { triggerGeneracionMasiva } from 'api/pae/ejecuciones';
import { ActionsNames, getModelPermission } from 'utils/permission';
import { ESTADOS_GLOBALES_MAP } from 'utils/estadosGlobales';

export const permissionConfig = {
  mainPermission: 'view_generacionpae',
  required: [
    'add_generacionpae',
    'change_generacionpae',
  ],
  catalogsModels: [
    'padron',
    'origendelcreditopae',
    'sistemas',
    'periodofiscal',
  ],
  customActions: [
    ...getPermissionActionsConfigsPadrones(ActionsNames.add),
    // ...getPermissionActionsConfigsPadrones(ActionsNames.change),
  ],
};

const formatPermission = (_permission) => ({
  ...getModelPermission('generacionpae', []),
  ..._permission,
});

const rules = {
  required: [{ required: true }],
};

const StyledCheckboxGroup = styled(Checkbox.Group)`
  width: 100%;
  margin-bottom: 10px;

  .ant-checkbox-wrapper {
    margin-bottom: 8px;
  }
`;

// Componente para renderizar las opciones del grupo de checkboxes
const CheckboxOptions = ({ checkboxOptions }) => (
  <Row gutter={10}>
    {checkboxOptions.map(({
      label,
      value,
      disabled,
      tooltip,
    }) => (
      <Col xs={24} sm={24} md={8} key={value}>
        <Checkbox value={value} disabled={disabled}>
          <Typography.Text>
            {label}
            {tooltip && (
            <Popover placement="top" content={tooltip}>
              <InfoCircleOutlined style={{ marginLeft: 4, color: 'gray' }} />
            </Popover>
            )}
          </Typography.Text>
        </Checkbox>
      </Col>
    ))}
  </Row>
);

CheckboxOptions.propTypes = {
  checkboxOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      tooltip: PropTypes.string,
    }),
  ).isRequired,
};

const initialCheckedValues = [
  // 'limitar_meses_sin_ejecuciones_previas',
  // 'limitar_por_adeudos',
  'limitar_por_claves_de_padron',
];

const excludeCanceled = (e) => e.estados_globales !== ESTADOS_GLOBALES_MAP.CANCELADO;

// eslint-disable-next-line react/prop-types
const GenerarEjecuciones = ({ permission: _permission }) => {
  const permission = formatPermission(_permission);

  const [ConfirmModal, confirm] = useConfirm('Confirmación', '¿Estás seguro de querer generar las ejecuciones?', {
    alert: 'Esta acción puede tardar varios minutos en completarse.',
  });
  const { padrones: _padrones } = useSelector((s) => s.catalogs);
  const padrones = _padrones.filter((p) => hasPadronPermission(p.id) && p.id !== 5
    && p?.usado_en_ejecuciones);
  const [tipoDePadron, setTipoDePadron] = useState(null);
  const [loading, setLoading] = useState(true);
  const [padronFound, setPadronFound] = useState(null);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [origenDeCredito, setOrigenDeCredito] = useState([]);
  const [sistema, setSistema] = useState([]);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);
  const [checkedValues, setCheckedValues] = useState(initialCheckedValues);
  const [origenSeleccionado, setOrigenSeleccionado] = useState(null);

  const [form] = Form.useForm();

  const identificadorLabel = (() => {
    if (!origenSeleccionado) return 'identificador del empadronado';
    const padronOrigen = padrones.find((p) => p.id === origenSeleccionado.padron) || {};
    const { campo_de_clave_principal } = padronOrigen;
    return campo_de_clave_principal || 'identificador del empadronado';
  })();

  const padronIndividualActivo = checkedValues.includes('generacion_de_padron_individual');

  const options = [
    {
      label: 'Limitar cantidad de reportes morosos',
      value: 'limitar_cantidad_de_ejecuciones',
      disabled: padronIndividualActivo,
      tooltip: 'Genera como máximo la cantidad indicada de reportes morosos en esta generación.',
    },
    {
      label: 'Excluir reportes morosos recientes',
      value: 'limitar_meses_sin_ejecuciones_previas',
      tooltip: 'Omite empadronados que ya tengan un reporte moroso en el periodo de meses indicado. No aplica si elige generación individual.',
    },
    {
      label: 'Generación de un solo empadronado',
      value: 'generacion_de_padron_individual',
      tooltip: 'Genera el reporte moroso únicamente para el empadronado que busque. Al activarla se deshabilitan los demás filtros de alcance.',
    },
    {
      label: `Rango de ${identificadorLabel}`,
      value: 'limitar_por_claves_principales',
      disabled: padronIndividualActivo,
      tooltip: `Filtra por un intervalo del campo «${identificadorLabel}» (identificador del
    empadronado, p. ej. placa o folio). Indique valor inicial y final.`,
    },
    {
      label: `Lista específica de ${identificadorLabel}`,
      value: 'usar_lista_de_claves_principales',
      disabled: padronIndividualActivo,
      tooltip: `Filtra solo los empadronados cuyo «${identificadorLabel}» coincida exactamente
    con la lista que pegue. Es independiente del rango; no use ambas a la vez si busca el
    mismo criterio.`,
    },
    {
      label: 'Rango de clave interna de padrón',
      value: 'limitar_por_claves_de_padron',
      disabled: padronIndividualActivo,
      tooltip: 'Filtra por el número de clave interna del padrón en el sistema. No es lo mismo que el identificador del negocio (placa, folio, etc.).',
    },
    {
      label: 'Limitar por rango de periodos fiscales',
      value: 'limitar_por_periodos_fiscales_anuales',
      tooltip: 'Solo considera adeudos de los años fiscales entre el periodo inicial y el final.',
    },
    {
      label: 'Limitar rango de adeudos',
      value: 'limitar_por_adeudos',
      tooltip: 'Solo genera ejecuciones cuyo importe de adeudo esté entre el mínimo y el máximo indicados.',
    },
  ];

  const onClear = () => {
    form.resetFields();
    setPadronFound(null);
    setTipoDePadron(null);
    setOrigenSeleccionado(null);
    setCheckedValues(initialCheckedValues);
  };

  const getPeriodoFiscalRules = () => ({
    periodoFiscalInicial: [
      { required: true, message: 'Seleccione el periodo fiscal inicial' },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value) return Promise.resolve();

          const periodoFinal = getFieldValue('periodo_fiscal_final');
          if (!periodoFinal) return Promise.resolve();

          const periodoInicial = periodosFiscales.find((p) => p.id === value);
          const periodoFinalObj = periodosFiscales.find((p) => p.id === periodoFinal);

          if (periodoInicial && periodoFinalObj
             && periodoInicial.periodo > periodoFinalObj.periodo) {
            return Promise.reject(
              new Error('El periodo fiscal inicial no puede ser mayor al final'),
            );
          }

          return Promise.resolve();
        },
      }),
    ],
    periodoFiscalFinal: [
      { required: true, message: 'Seleccione el periodo fiscal final' },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value) return Promise.resolve();

          const periodoInicial = getFieldValue('periodo_fiscal_inicial');
          if (!periodoInicial) return Promise.resolve();

          const periodoInicialObj = periodosFiscales.find((p) => p.id === periodoInicial);
          const periodoFinal = periodosFiscales.find((p) => p.id === value);

          if (periodoInicialObj && periodoFinal
             && periodoInicialObj.periodo > periodoFinal.periodo) {
            return Promise.reject(
              new Error('El periodo fiscal final no puede ser menor al inicial'),
            );
          }

          return Promise.resolve();
        },
      }),
    ],
  });

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      const [
        origenesDeCredito,
        sistemas,
        _periodosFiscales,
      ] = await Promise.all([
        getOrigenesDeCredito(),
        getSistemas(),
        getPeriodosFiscales(),
      ]);

      if (mounted) {
        setOrigenDeCredito(origenesDeCredito.filter(excludeCanceled));
        setSistema(sistemas);
        setPeriodosFiscales(_periodosFiscales);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const displayTitle = (id) => padrones.find((e) => e.id === id)?.descripcion;

  const formatPadrones = async (_padron) => {
    const nombre_completo = padronToString(_padron, tipoDePadron);
    setPadronFound({ ..._padron, nombre_completo });
    form.setFieldsValue({ object_padron_individual_id: _padron.id });
  };

  const handleSearchBusqueda = async () => {
    try {
      setLoading(true);
      const { q } = form.getFieldsValue();

      if (!q) {
        message.warn('Ingrese al menos un caracter para buscar.');
        setPadronFound(null);
        return;
      }

      const found = await getPadronesMap[tipoDePadron]({ q });
      const results = found?.results || found;

      if (results?.length === 1) {
        const [first] = results;
        await formatPadrones(first);
      } else if (results?.length > 1) {
        setPadronFound(found);
        setVisibleBusquedaPadrones(true);
        setPadronFound(null);
      } else {
        message.warn('No se encontró ningún padron relacionado.');
        setPadronFound(null);
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async () => {
    try {
      if (!checkedValues.length) {
        message.warn('No se seleccionaron opciones para generar las ejecuciones. Por favor, seleccione al menos una opción.');
        return;
      }

      const confirmed = await confirm();
      if (!confirmed) return;
      setLoading(true);
      const values = form.getFieldsValue();

      if (checkedValues.includes('limitar_por_periodos_fiscales_anuales')) {
        const { periodo_inicial, periodo_final } = values;
        values.periodo_inicial = periodosFiscales.find((p) => p.id === periodo_inicial).periodo;
        values.periodo_final = periodosFiscales.find((p) => p.id === periodo_final).periodo;
      }
      options.forEach((item) => {
        values[item.value] = !!checkedValues.find((i) => i === item.value);
      });

      const generacion = await postGeneracion({
        ...values,
        padron: values.tipo_de_padron,
      });

      if (generacion) {
        const result = await triggerGeneracionMasiva(generacion.id);
        if (result.success) {
          message.success(result.message);
          onClear();
        } else {
          message.warn(result.message);
        }
      }
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const normalizeListaDeClavesPrincipales = (value) => {
    if (!value) return value;
    // Reemplazar espacios y enters con comas
    let normalized = value.replace(/[\s\n]+/g, ',');
    // Eliminar comas consecutivas
    normalized = normalized.replace(/,+/g, ',');
    normalized = normalized.toUpperCase();
    // Finalmente limpiar espacios extra
    normalized = normalized.replace(/\s+/g, '');
    // Eliminar comas al inicio y final
    // normalized = normalized;
    return normalized;
  };

  return (
    <Spin spinning={loading} tip="Cargando datos...">
      <Card
        bordered={false}
        extra={(
          <FormSubmitCustomControls
            loading={loading}
            controls={[
              {
                icon: <DeleteOutlined />,
                text: 'Limpiar formulario',
                onClick: () => {
                  onClear();
                },
              },
              {
                icon: <FileSyncOutlined />,
                text: 'Generar Reportes',
                onClick: () => {
                  form.submit();
                },
                disabled: !permission.add,
              },
            ]}
          />
      )}
      >
        <Form
          form={form}
          layout="vertical"
          name="generar-ejecuciones"
          onFinish={onFinish}
          scrollToFirstError={{
            behavior: 'smooth',
            block: 'center',
            inline: true,
          }}
          initialValues={{
            cantidad_de_ejecuciones: 100,
            importe_de_adeudo_final: 99999999999999,
            importe_de_adeudo_inicial: 500,
            cantidad_de_meses: 1,
          }}
        >
          {/* Sección Principal */}
          <Row gutter={[10, 10]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="origen_del_credito"
                label="Origen del crédito"
                rules={[{ required: true, message: 'Seleccione el origen del crédito' }]}
              >
                <Select
                  dataSource={origenDeCredito}
                  onChange={(value) => {
                    if (!value) {
                      setOrigenSeleccionado(null);
                      setTipoDePadron(null);
                      return;
                    }

                    const origen = origenDeCredito.find((o) => o.id === value);
                    if (origen) {
                      form.setFieldValue('sistema', origen.sistema);
                      form.setFieldValue('tipo_de_padron', origen.padron);
                      setTipoDePadron(origen.padron);
                      setOrigenSeleccionado(origen);
                    }
                  }}
                />
              </Form.Item>
              {origenSeleccionado && (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: -16, marginBottom: 16 }}>
                Padrón:
                {' '}
                {padrones.find((p) => p.id === origenSeleccionado.padron)?.descripcion}
                {' · '}
                Sistema:
                {' '}
                {sistema.find((s) => s.id === origenSeleccionado.sistema)?.descripcion}
              </Typography.Text>
              )}
              <div style={{ display: 'none' }}>
                <Form.Item name="tipo_de_padron" label="Tipo de padrón">
                  <Select dataSource={padrones} disabled />
                </Form.Item>
                <Form.Item name="sistema" label="Sistema" rules={rules.required}>
                  <Select dataSource={sistema} disabled />
                </Form.Item>
              </div>
            </Col>
          </Row>
          <Row gutter={[10, 10]}>
            <Col span={24}>
              <Form.Item
                name="observaciones"
                label="Observaciones"
              >
                <Input
                  allowClear
                  maxLength={500}
                />
              </Form.Item>
            </Col>
          </Row>
          <StyledCheckboxGroup
            value={checkedValues}
            onChange={async (values) => {
              try {
                if (values.includes('generacion_de_padron_individual') && !form.getFieldValue('origen_del_credito')) {
                  await form.validateFields(['origen_del_credito']);
                }

                if (values.includes('generacion_de_padron_individual') && !checkedValues.includes('generacion_de_padron_individual')) {
                  setCheckedValues(['generacion_de_padron_individual']);
                  return;
                }

                setCheckedValues(values);
              } catch (err) {
                onError(err);
              }
            }}
          >
            <CheckboxOptions checkboxOptions={options} />
          </StyledCheckboxGroup>

          <Row gutter={[10, 10]}>
            {checkedValues.includes('limitar_cantidad_de_ejecuciones') && (
            <Col xs={24} md={8}>
              <NumericInput
                rules={[
                  { required: true, message: 'Ingrese la cantidad de ejecuciones' },
                  {
                    validator: async (_, value) => {
                      if (value > 2000) {
                        throw new Error('La cantidad de ejecuciones no puede ser mayor a 2000');
                      }
                    },
                  },
                ]}
                name="cantidad_de_ejecuciones"
                label="Cantidad de ejecuciones"
              />
            </Col>
            )}

            {checkedValues.includes('limitar_meses_sin_ejecuciones_previas') && (
            <Col xs={24} md={8}>
              <NumericInput
                name="cantidad_de_meses"
                label="Meses hacia atrás sin reporte moroso"
                rules={rules.required}
                tooltip="Solo incluye empadronados sin reporte moroso en ese lapso. Con 1 se revisa solo el mes en curso; con 3, el mes actual y los dos anteriores."
              />
            </Col>
            )}

            {checkedValues.includes('generacion_de_padron_individual') && (
            <InputSearchPro
              rules={rules.required}
              prefix={{
                rules: rules.required,
                style: { width: '100%' },
                name: 'tipo_de_padron',
                label: 'padron',
                dataSource: padrones,
                onChange: (e) => setTipoDePadron(e),
                disabled: true,
              }}
              name="q"
              onClickAdvanced={hasAdvanceSearchPermissions(tipoDePadron)
                ? () => setVisibleBusquedaPadrones(true) : null}
              label={displayTitle(tipoDePadron)}
              inputProps={{
                onPressEnter: () => handleSearchBusqueda(),
              }}
              breakPoints={{
                xs: 24,
                sm: 24,
                md: 16,
              }}
              resultProps={{
                breakPoints: {
                  xs: 24,
                  sm: 24,
                  md: 16,
                },
                name: 'object_padron_individual_id',
                label: displayTitle(tipoDePadron),
                labelProp: 'nombre_completo',
                dataSource: padronFound,
                onClear: () => {
                  setPadronFound(null);
                  setVisibleBusquedaPadrones(false);
                  form.setFieldsValue({
                    object_padron_individual_id: null,
                  });
                },
              }}
            />
            )}
          </Row>

          <Row gutter={[10, 10]}>

            {checkedValues.includes('limitar_por_claves_principales') && (
            <>
              <Col xs={12} md={8}>
                <Form.Item
                  name="clave_principal_inicial"
                  label={`${identificadorLabel} inicial`}
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="clave_principal_final"
                  label={`${identificadorLabel} final`}
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
            </>
            )}
          </Row>

          {checkedValues.includes('usar_lista_de_claves_principales') && (
          <Row gutter={[10, 10]}>
            <Col xs={12} md={16}>
              <Form.Item
                name="lista_de_claves_principales"
                label={`Lista de ${identificadorLabel} (separados por coma)`}
                rules={rules.required}
                tooltip={(
                  <Row>
                    <Col span={24}>
                      Deberá pegar una lista de claves principales separadas por comas (,)
                      y sin espacios en blanco.
                    </Col>
                    <Col span={24}>
                      Ejemplo:
                    </Col>
                    <Col span={24}>
                      <strong>
                        AAAA1234567890,BBBB1234567890,CCCC1234567890
                      </strong>
                    </Col>
                    <Col span={24}>
                      Se buscarán claves iguales a las proporcionadas.
                    </Col>
                  </Row>
                )}
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 2, maxRows: 2 }}
                  onChange={(e) => {
                    form.setFieldValue('lista_de_claves_principales', normalizeListaDeClavesPrincipales(e.target.value));
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          )}

          <Row gutter={[10, 10]}>
            {checkedValues.includes('limitar_por_claves_de_padron') && (
            <>
              <Col xs={12} md={8}>
                <Form.Item
                  name="clave_de_padron_inicial"
                  label="Clave interna de padrón inicial"
                  tooltip="Número interno del registro en el padrón, no el identificador del negocio."
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="clave_de_padron_final"
                  label="Clave interna de padrón final"
                  tooltip="Número interno del registro en el padrón, no el identificador del negocio."
                  rules={rules.required}
                >
                  <Input allowClear />
                </Form.Item>
              </Col>
            </>
            )}
          </Row>

          <Row gutter={[10, 10]}>
            {checkedValues.includes('limitar_por_periodos_fiscales_anuales') && (
            <>
              <Col xs={12} md={8}>
                <Form.Item
                  name="periodo_inicial"
                  label="Periodo fiscal inicial"
                  rules={getPeriodoFiscalRules().periodoFiscalInicial}
                >
                  <Select
                    dataSource={periodosFiscales.sort((a, b) => b.periodo - a.periodo)}
                    render={(e) => e.periodo}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} md={8}>
                <Form.Item
                  name="periodo_final"
                  label="Periodo fiscal final"
                  rules={getPeriodoFiscalRules().periodoFiscalFinal}
                >
                  <Select
                    dataSource={periodosFiscales.sort((a, b) => b.periodo - a.periodo)}
                    render={(e) => e.periodo}
                  />
                </Form.Item>
              </Col>
            </>
            )}
          </Row>

          <Row gutter={[10, 10]}>

            {checkedValues.includes('limitar_por_adeudos') && (
            <>
              <Col xs={12} md={8}>
                <NumericInput
                  name="importe_de_adeudo_inicial"
                  label="Adeudo inicial"
                  currency
                  rules={[
                    { required: true, message: 'Ingrese el importe inicial' },
                  ]}
                />
              </Col>
              <Col xs={12} md={8}>
                <NumericInput
                  name="importe_de_adeudo_final"
                  label="Adeudo final"
                  currency
                  rules={rules.required}
                />
              </Col>
            </>
            )}
          </Row>
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={formatPadrones}
          tipoDePadron={tipoDePadron}
          allowAdd
        />
      </Card>
      <ConfirmModal />
    </Spin>
  );
};

export default GenerarEjecuciones;
